import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { combineLatest, filter, map, Observable, tap } from 'rxjs';

import { ConfigService } from '../../../config.service';
import { getRoleMatrix } from '../../container.utils';
import { ContainerAccess } from '../../enums/container-access.enum';
import { SiteRole } from '../../enums/site-role.enum';
import { ProjectWithStats } from '../../models/project.model';
import { ProfileService } from '../../../profile/profile.service';
import { ProjectsService } from '../../../projects/projects.service';
import { RoutingHelperService } from '../../../routing-helper.service';
import { SearchMode, SidebarService } from '../../../sidebar.service';

@Component({
  selector: 'side-bar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  host: {
    class: 'vision-dark-theme',
  },
})
export class SidebarComponent implements OnInit {

  centralEnabled = this.config.getCentralConfig().enabled;
  collectionsEnabled = this.config.isFeatureEnabled('collections');
  dataViewsNon21CFREnabled = this.config.isFeatureEnabled('data_views_non_21CFR');
  dicomUploadEnabled = this.config.isFeatureEnabled('legacy_dicom_uploader');
  exchangeAdminEnabled = this.config.isFeatureEnabled('exchange_admin');
  exchangeEnabled = this.config.isFeatureEnabled('exchange');
  importEnabled = this.config.isFeatureEnabled('project_import');
  isSiteAdmin$ = this.profileService.siteAdmin$;
  openWidth = '200px';
  tasksEnabled = this.config.isFeatureEnabled('reader_tasks');
  tasksRefactorEnabled = this.config.isFeatureEnabled('tasks_refactor');

  get showNewTasks(): boolean {
    return this.tasksRefactorEnabled;
  }

  get showOldTasksOnly(): boolean {
    return this.tasksEnabled && !this.tasksRefactorEnabled;
  }

  get showLegacyTasks(): boolean {
    return this.tasksEnabled && this.tasksRefactorEnabled;
  }

  constructor(
    public config: ConfigService,
    public projectsService: ProjectsService,
    public profileService: ProfileService,
    private changeDetector: ChangeDetectorRef,
    private sidebarService: SidebarService,
    private routeHelper: RoutingHelperService,
  ) {}

  ngOnInit(): void {
    // required to support providing an initial prev state for app-side-bar-search
    this.sidebarService.stateChange$.subscribe();
  }

  exhaustiveMode$ = this.profileService.exhaustiveMode$.pipe(
    // needed to make sure the background color classes are updated on change
    tap(() => this.changeDetector.detectChanges())
  );

  taskCount$ = this.profileService.getUserTaskCount();

  recentProjects$: Observable<ProjectWithStats[]> = combineLatest([this.projectsService.getProjects(), this.projectsService.visitedProjects$])
    .pipe(
      map(([projects, recentProjects]) => {
        const lastFourProjectIds = recentProjects.slice(0, 3);
        // need to preserve the order of the recent projects
        return lastFourProjectIds.map(id => projects.find((project) => project._id === id)).filter(project => project !== undefined);
      })
    );

  showGroups$ = combineLatest([
    this.profileService.getProfile().pipe(filter(data => Boolean(data))),
    this.projectsService.getGroups(),
  ]).pipe(
    map(([profile, groups]) => {
      const isSiteAdmin = SiteRole.isSiteAdmin(profile);
      const isGroupAdmin = isSiteAdmin || groups.some(group => {
        const roleMatrix = getRoleMatrix(group, profile);
        return roleMatrix[ContainerAccess.Admin];
      });
      const hasGroupRead = groups.some(group => {
        const roleMatrix = getRoleMatrix(group, profile);
        return roleMatrix[ContainerAccess.Admin] ||
          roleMatrix[ContainerAccess.ReadOnly] ||
          roleMatrix[ContainerAccess.ReadWrite];
      });

      return isSiteAdmin || isGroupAdmin || hasGroupRead;
    })
  );

  isSelected(location: string): boolean {
    return window.location.hash.startsWith('#/' + location);
  }

  openSearch(): void {
    const params = this.routeHelper.getGlobalRouteParams();
    this.sidebarService.search.searchMode = SearchMode.Basic;
    this.sidebarService.search.structuredQuery = '';
    this.sidebarService.projectContext$.next(params?.projectId || null);
  }
}
